// @file Global snackbar store
import window from '@@/bits/global'
import { __ } from '@@/bits/intl'
import { SnackbarNotificationType } from '@@/enums'
import type { DispatchableAction } from '@@/types'
import { uniqueId } from 'lodash-es'
import { defineStore } from 'pinia'
import { ref } from 'vue'

export interface GlobalSnackbarNotification {
  notificationType?: SnackbarNotificationType
  message: string
  timeout?: number
  persist?: boolean
  iconName?: string
  actionText?: string
  actionTextActions?: Array<DispatchableAction | Function>
  shouldTruncate?: boolean
}

// Internal type
interface QueuedGlobalSnackbarNotification extends GlobalSnackbarNotification {
  uid: String
  // The snackbar will occupy the originalIndex-th spot on the screen.
  // Since snackbars may be removed, a particular snackbar's position within snackbarArray might change.
  // In order to ensure snackbars don't shift around and don't overlap,
  // we assign a fixed originalIndex when it's originally/first added to the array.
  originalIndex: number
}

const DEFAULT_TIMEOUT = 3000

const snackbarNotificationTypePresets: Record<
  SnackbarNotificationType,
  { [P in keyof QueuedGlobalSnackbarNotification]?: QueuedGlobalSnackbarNotification[P] }
> = {
  [SnackbarNotificationType.error]: {
    iconName: 'exclamation',
  },
  [SnackbarNotificationType.success]: {
    iconName: 'checkmark',
  },
}

export const useGlobalSnackbarStore = defineStore('globalSnackbar', () => {
  const snackbarArray = ref([] as QueuedGlobalSnackbarNotification[])

  function setSnackbar(newSnackbar: GlobalSnackbarNotification): string {
    const uid = uniqueId('snack_')

    const originalIndex =
      snackbarArray.value.length === 0
        ? 0
        : Math.max(...snackbarArray.value.map((snackbar) => snackbar.originalIndex)) + 1

    const presetAttributes =
      newSnackbar.notificationType == null ? null : snackbarNotificationTypePresets[newSnackbar.notificationType]

    const newSnackbarNotification: QueuedGlobalSnackbarNotification = {
      ...newSnackbar,
      uid,
      originalIndex,
      ...presetAttributes,
    }

    snackbarArray.value = [...snackbarArray.value, newSnackbarNotification]

    // If timeout is set or if persist is falsey, set snackbar to expire automatically
    if (newSnackbar.timeout != null || newSnackbar.persist !== true) {
      window.setTimeout(() => {
        snackbarArray.value = snackbarArray.value.filter((snackbar) => snackbar.uid !== uid)
      }, newSnackbar.timeout ?? DEFAULT_TIMEOUT)
    }

    return uid
  }

  function removeSnackbar(snackbarToRemoveUid: string): void {
    snackbarArray.value = snackbarArray.value.filter((snackbar) => snackbar.uid !== snackbarToRemoveUid)
  }

  function executeActions(actions: Array<DispatchableAction | Function> = [], event: Event): void {
    actions.forEach((actionHash): void => {
      if (typeof actionHash === 'function') {
        actionHash(event)
      } else {
        console.error('executeActions not yet implemented for dispatchable action!')
        // dispatch(actionHash.type, actionHash.payload, { root: true })
      }
    })
  }

  function genericFetchError(): void {
    setSnackbar({
      message: __('Oops! Something went wrong. Please try again.'),
      notificationType: SnackbarNotificationType.error,
    })
  }

  function isMessageBeingShown(message): boolean {
    return snackbarArray.value.some((snackbar) => snackbar.message === message)
  }

  return {
    snackbarArray,
    setSnackbar,
    removeSnackbar,
    executeActions,
    genericFetchError,
    isMessageBeingShown,
  }
})

export { SnackbarNotificationType }
export type { QueuedGlobalSnackbarNotification }
